import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from './firebase'; // Ensure db is imported
import { collection, addDoc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import './AuraCalculator.css';

import gainSfx from './assets/create.mp3';
import magicSfx from './assets/magic.mp3';
import lossSfx from './assets/delete.mp3';

export default function AuraCalculator() {
  const [moments, setMoments] = useState([]);
  const [totalAura, setTotalAura] = useState(0);
  const [history, setHistory] = useState([]); // Persistent history
  const [pendingClaims, setPendingClaims] = useState([]); // Buffer for saving
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (!auth.currentUser) return;
    const q = query(collection(db, 'users', auth.currentUser.uid, 'auraHistory'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      const pastMoments = snap.docs.map(doc => doc.data());
      setHistory(pastMoments);
      // Calculate total from history
      const historyTotal = pastMoments.reduce((acc, curr) => acc + curr.points, 0);
      setTotalAura(historyTotal);
    });
    return () => unsubscribe();
  }, []);

  // ✅ Persist audio refs
  const audioGain = useRef(new Audio(gainSfx));
  const audioMagic = useRef(new Audio(magicSfx));
  const audioLoss = useRef(new Audio(lossSfx));

  // ─────────────────────────────
  // Fetch Aura Moments (secure)
  // ─────────────────────────────
  const fetchAura = async () => {
    const user = auth.currentUser;
    if (!user) {
      setError("Please log in to calculate aura.");
      return;
    }
    audioMagic.current.currentTime = 0;
  audioMagic.current.play().catch(() => {});
    setLoading(true);
    setError(null);

    try {
      await user.getIdToken(true); // ensure fresh auth

      const genAura = httpsCallable(
        getFunctions(),
        'generateAuraMoments'
      );

      const res = await genAura({ userId: user.uid });

      // 🛡️ Defensive validation
      const data = Array.isArray(res.data) ? res.data : [];

      const sanitized = data.filter(m =>
        typeof m?.text === 'string' &&
        typeof m?.points === 'number' &&
        (m.type === 'gain' || m.type === 'loss')
      );

      setMoments(sanitized.slice(0, 15)); // hard cap
    } catch (err) {
      console.error("Aura fetch failed:", err);

      if (
        err.code === 'resource-exhausted' ||
        err.message?.includes('LIMIT_REACHED')
      ) {
        setError("Daily aura limit reached. Try tomorrow.");
      } else {
        setError("Could not analyze aura right now.");
      }
    } finally {
      setLoading(false);
    }
  };
  const getVibeMessage = () => {
    if (totalAura > 10000) return "ABSOLUTE PEAK ENERGY";
    if (totalAura > 3000) return "VIBE CHECK: PASSED";
    if (totalAura < 0) return "CRITICAL CRINGE LEVELS";
    return "STAYING BASED";
  };

  // ─────────────────────────────
  // Claim Aura Moment (safe)
  // ─────────────────────────────
  const claimMoment = (idx, moment) => {
    // audio
   const audio = moment.type === 'gain' ? audioGain.current : audioLoss.current;
   audio.currentTime = 0;
    audio.play().catch(() => {});
    // clamp aura to sane range
   setPendingClaims(prev => [...prev, moment]);
    setTotalAura(prev => prev + moment.points);
    setMoments(prev => prev.filter((_, i) => i !== idx));
  };
  // 3. Save Logic: Bulk write to Firestore
  const saveAura = async () => {
    if (pendingClaims.length === 0) return;
    setIsSaving(true);
    try {
      const colRef = collection(db, 'users', auth.currentUser.uid, 'auraHistory');
      // Save all pending items
      await Promise.all(pendingClaims.map(m => 
        addDoc(colRef, { ...m, createdAt: serverTimestamp() })
      ));
      setPendingClaims([]); // Clear buffer after successful save
    } catch (err) {
      setError("Failed to save aura.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="aura-wrapper">
      <motion.div
        className="aura-header"
        animate={{ scale: [1, 1.05, 1] }}
      >
        <h1 className="aura-score">
          {totalAura.toLocaleString()}
        </h1>
        <p className="aura-label">CURRENT AURA</p>
        <p className="vibe-msg">{getVibeMessage()}</p>
      </motion.div>

      {error && <p className="aura-error">{error}</p>}
      {moments.length > 0 && (
  <p className="aura-disclaimer">
     Only claim the moments that truly found you. <br/> 
    <span className="cursive">Integrity is its own aura.</span>
  </p>
)}
      {/* Suggestion Grid (Top) */}
      <AnimatePresence>
        {moments.length > 0 && (
          <div className="moments-grid">
            {moments.map((m, i) => (
              <motion.button
  key={`${m.text}-${i}`}
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ scale: 0, opacity: 0 }}
  whileTap={{ scale: 0.95 }} // Visual feedback on click
  className={`moment-card ${m.type}`}
  onClick={() => claimMoment(i, m)} // m is the whole object {text, points, type}
>
  <span className="moment-text">{m.text}</span>
  <span className="moment-points">
    {m.points > 0 ? `+${m.points}` : m.points}
  </span>
</motion.button>

            ))}
          </div>
        )}
      </AnimatePresence>
      <div className="aura-actions">
        {loading ? (
    /* New Elegant Loading State */
    <button className="gen-btn loading-shimmer" disabled>
       Generating moments
    </button>
  ) : moments.length === 0 ? (
    <button onClick={fetchAura} className="gen-btn">REFRESH VIBES</button>
  ) : (
    <button onClick={saveAura} className="save-btn" disabled={isSaving}>
      {isSaving ? "LOCKING IN..." : "SAVE MY AURA"}
    </button>
  )}
      </div>

     {/* History List (Bottom) */}
      <div className="aura-history">
        <h3>AURA LEGACY</h3>
        {history.map((h, i) => (
          <div key={i} className={`history-item ${h.type}`}>
            <span>{h.text}</span>
            <span>{h.points > 0 ? `+${h.points}` : h.points}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
